import { useCallback, useState } from 'react';
import {
  isErpTaskSubmissionResponse,
  isTaskPollingCancelledError,
  postErpAsyncTask,
  waitForErpTasks
} from '@/lib/task-api';
import { useTaskPollingSignal } from '@/hooks/use-task-polling-signal';
import type { ColumnMapping, ValidationError } from '@/lib/excel';
import { toast, useExcel, useExcelProcessor } from '@/hooks';
import { useAuth } from '@/contexts/auth-context';
import { QUERY_KEYS } from '@/constants';
import { ExcelConfig } from '../utils';

interface UseHoaDonBanHangImportProps {
  closeImportDialog: () => void;
  refreshData: () => Promise<void>;
  transactionOptions: { value: string; label: string }[];
}

export const useHoaDonBanHangImport = ({
  closeImportDialog,
  refreshData,
  transactionOptions
}: UseHoaDonBanHangImportProps) => {
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [errorData, setErrorData] = useState<{
    errors: ValidationError[];
    columnMapping: Record<string, ColumnMapping>;
  } | null>(null);
  const { entityUnit, entity } = useAuth();
  const { processFile } = useExcelProcessor(ExcelConfig);
  const { getUUIDByCode } = useExcel();
  const getTaskPollingSignal = useTaskPollingSignal();

  const handleImportBodies = useCallback(
    async (bodies: BanHangRecord[]) => {
      const BATCH_SIZE = 500;
      let totalCreated = 0;
      let totalFailed = 0;
      let hasError = false;

      try {
        if (!entity?.slug) {
          toast({
            description: 'Không tìm thấy thông tin công ty',
            variant: 'destructive'
          });
          return;
        }

        for (let i = 0; i < bodies.length; i += BATCH_SIZE) {
          const batch = bodies.slice(i, i + BATCH_SIZE);
          let data: any;

          try {
            const response = await postErpAsyncTask<any>(
              `/entities/${entity?.slug}/erp/${QUERY_KEYS.HOA_DON_BAN_HANG}/bulk-create/`,
              {
                list: batch
              }
            );
            data = response.data;
          } catch (error) {
            console.error('Bulk create error:', error);
            totalFailed += batch.length;
            hasError = true;
            throw error;
          }

          if (isErpTaskSubmissionResponse(data)) {
            const [taskResult] = await waitForErpTasks<{ success?: boolean; created?: number; failed?: number }>({
              entitySlug: entity.slug,
              tasks: [data],
              intervalMs: 2000,
              signal: getTaskPollingSignal()
            });
            const result = taskResult.result;

            if (result && typeof result === 'object' && 'success' in result) {
              totalCreated += result.created ?? 0;
              totalFailed += result.failed ?? 0;
              if (!result.success) hasError = true;
            } else {
              totalCreated += 1;
            }
            continue;
          }

          if (data && typeof data === 'object' && 'success' in data) {
            totalCreated += data.created ?? 0;
            totalFailed += data.failed ?? 0;
            if (!data.success) hasError = true;
          } else {
            totalCreated += Array.isArray(data) ? data.length : 0;
          }
        }

        if (hasError && totalCreated === 0) {
          toast({
            description: 'Không thể nhập hóa đơn. Vui lòng kiểm tra dữ liệu và thử lại.',
            variant: 'destructive'
          });
        } else {
          toast({
            description:
              totalFailed === 0
                ? 'Đã nhập hóa đơn từ Excel thành công'
                : 'Đã nhập hóa đơn từ Excel, một số chứng từ có lỗi',
            variant: 'default'
          });
        }
      } catch (error) {
        if (isTaskPollingCancelledError(error)) return;
        console.error('Import error:', error);
        toast({
          description: error instanceof Error ? error.message : 'Không thể nhập hóa đơn. Vui lòng thử lại.',
          variant: 'destructive'
        });
        throw error;
      } finally {
        await refreshData();
        setIsImporting(false);
      }
    },
    [entity?.slug, getTaskPollingSignal, refreshData]
  );

  const handleImportFromExcel = useCallback(
    async (data: { errors?: string; file: File | null; sheetIndex?: string }) => {
      setIsImporting(true);

      if (!entityUnit?.uuid) {
        toast({
          description: 'Không tìm thấy thông tin đơn vị',
          variant: 'destructive'
        });
        setIsImporting(false);
        return;
      }

      if (!data.file) {
        toast({
          description: 'Vui lòng chọn file Excel',
          variant: 'destructive'
        });
        setIsImporting(false);
        return;
      }

      try {
        const maxErrors = parseInt(data.errors || '200', 10);
        const sheetIndex = parseInt(data.sheetIndex || '0', 10);

        const result = await processFile({
          file: data.file,
          sheetIndex,
          maxErrors,
          getUUIDByCode,
          context: {
            entityUnitUUID: entityUnit.uuid,
            defaultMaGD: transactionOptions[0]?.value || 'HD'
          }
        });

        if (result.success && result.data) {
          await handleImportBodies(result.data);
          closeImportDialog();
          return;
        }

        if (result.errors && result.columnMapping) {
          setErrorData({ errors: result.errors, columnMapping: result.columnMapping });
          setShowErrorDialog(true);
          closeImportDialog();
        }
      } finally {
        setIsImporting(false);
      }
    },
    [closeImportDialog, entityUnit?.uuid, getUUIDByCode, handleImportBodies, processFile, transactionOptions]
  );

  const handleImportErrorClose = useCallback(() => {
    setShowErrorDialog(false);
    setErrorData(null);
  }, []);

  return {
    isImporting,
    importErrorDialog: {
      open: showErrorDialog,
      errorData,
      onClose: handleImportErrorClose
    },
    handleImportFromExcel
  };
};
